import React, { useState, useEffect } from 'react';
import PersonIcon from '@mui/icons-material/Person';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from './AuthProvider';
import { useBranchOverride } from './BranchOverrideContext';
import { useUserRole } from './UserRoleProvider';
import { franchiseApi, type FranchiseContact } from '../services/franchiseApi';
import { adminApi } from '../services/adminApi';
import type { AdminUser } from '../types/admin';
import './AuthStatus.css';

interface ContactWithAgency extends FranchiseContact {
  agencyUuid: string;
  agencyName: string;
  // Every branch the person belongs to. Impersonation targets the person (by email),
  // not a single branch, so we keep all their branch codes to show and search on.
  branchCodes: number[];
}

const AuthStatus: React.FC = () => {
  const { user, logout } = useAuth();
  const { isAdmin, isCorporate } = useUserRole();
  const { overrideUuid, overrideEmail, setOverride } = useBranchOverride();
  const [showMenu, setShowMenu] = useState(false);
  const [allContacts, setAllContacts] = useState<ContactWithAgency[]>([]);
  const [dbUsers, setDbUsers] = useState<AdminUser[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [pickerTab, setPickerTab] = useState<'db' | 'franchise'>('db');

  // The tab controls are admin-only, so a non-admin can never move off the tab they start on:
  // the 'db' default left corporate non-admins on the admin-only 'All Users' tab with no way to
  // reach the franchise contacts they can use. Derived rather than an initial state because roles
  // resolve asynchronously — isAdmin is false on first render even for an admin.
  const activeTab = isAdmin ? pickerTab : 'franchise';

  useEffect(() => {
    if (!showPicker) return;

    // /v2/admin/users is admin-only (FranchiseApi gates it on manage/Statements, granted to
    // super-user and admin — the same rule `isAdmin` states here). The picker itself opens for
    // any corporate user, so without this check a corporate non-admin asked for a list they are
    // not allowed and the 403 surfaced as an unhandled rejection (WIPE-1431). The list only ever
    // fed the admin-gated 'All Users' tab, so not requesting it costs them nothing.
    if (isAdmin && dbUsers.length === 0 && adminApi.isConfigured() && user?.email) {
      adminApi.getUsers()
        .then(res => setDbUsers(res.users))
        .catch(() => {
          // Silently fail — the tab shows its empty state. The franchise-contacts tab is
          // fetched separately below and is unaffected.
        });
    }

    if (allContacts.length === 0 && franchiseApi.isConfigured()) {
      setLoadingContacts(true);
      // One request: all active contacts across open branches, each with its branch.
      franchiseApi.getAllBranchContacts().then(res => {
        // The endpoint returns one row per (contact, branch) pairing, so a person tied
        // to several branches comes back multiple times. The picker impersonates a
        // person, not a person-at-a-branch, so collapse to one row per hubspot_id,
        // gathering all their branch codes onto the single row.
        const byContact = new Map<string, ContactWithAgency>();
        for (const c of res.contacts || []) {
          if (!c.branch) continue;
          const existing = byContact.get(c.hubspot_id);
          if (existing) {
            if (!existing.branchCodes.includes(c.branch.branch_code)) {
              existing.branchCodes.push(c.branch.branch_code);
            }
          } else {
            byContact.set(c.hubspot_id, {
              ...c,
              agencyUuid: c.branch.id,
              agencyName: c.branch.dba_name || c.branch.name,
              branchCodes: [c.branch.branch_code],
            });
          }
        }
        const contactsList = Array.from(byContact.values());
        contactsList.forEach(c => c.branchCodes.sort((a, b) => a - b));
        contactsList.sort((a, b) => `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`));
        setAllContacts(contactsList);
      }).finally(() => setLoadingContacts(false));
    }
  }, [showPicker, isAdmin]);

  if (!user) return null;

  const displayName = user.name || user.email;
  const currentOverrideName = overrideEmail
    ? allContacts.find(c => c.email.toLowerCase() === overrideEmail.toLowerCase())
    : null;

  const filteredContacts = allContacts.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      `${c.firstname} ${c.lastname}`.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.branchCodes.some(code => String(code).includes(q)) ||
      c.agencyName.toLowerCase().includes(q)
    );
  });

  const filteredDbUsers = dbUsers.filter(u => {
    if (!isAdmin && u.roles.includes('hr-confidential')) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.roles.some(r => r.toLowerCase().includes(q))
    );
  });

  const handleSelect = (contact: ContactWithAgency | null) => {
    if (contact) {
      setOverride(contact.agencyUuid, contact.email);
    } else {
      sessionStorage.removeItem('admin-login-recorded');
      setOverride(null, null);
    }
    setShowPicker(false);
    setSearch('');
    window.location.reload();
  };

  const handleSelectDbUser = (dbUser: AdminUser) => {
    setOverride(null, dbUser.email);
    setShowPicker(false);
    setSearch('');
    window.location.reload();
  };

  return (
    <div className="auth-status">
      {showPicker && (
        <div className="branch-picker">
          <div className="branch-picker-header">
            <strong>Switch User</strong>
            <button onClick={() => setShowPicker(false)} className="branch-picker-close">&times;</button>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(32,203,212,0.15)', padding: '0 0.5rem' }}>
              <button onClick={() => setPickerTab('db')} style={{ flex: 1, padding: '0.4rem', fontSize: '0.65rem', fontWeight: 600, background: 'none', border: 'none', color: pickerTab === 'db' ? '#20cbd4' : 'rgba(32,203,212,0.5)', borderBottom: pickerTab === 'db' ? '2px solid #20cbd4' : '2px solid transparent', cursor: 'pointer' }}>All Users</button>
              <button onClick={() => setPickerTab('franchise')} style={{ flex: 1, padding: '0.4rem', fontSize: '0.65rem', fontWeight: 600, background: 'none', border: 'none', color: pickerTab === 'franchise' ? '#20cbd4' : 'rgba(32,203,212,0.5)', borderBottom: pickerTab === 'franchise' ? '2px solid #20cbd4' : '2px solid transparent', cursor: 'pointer' }}>Franchise Contacts</button>
            </div>
          )}
          <input
            type="text"
            placeholder={activeTab === 'db' ? 'Search by name, email, or role...' : 'Search by name, email, or branch...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="branch-picker-search"
            autoFocus
          />
          <div className="branch-picker-list">
            {(overrideUuid || overrideEmail) && (
              <button className="branch-picker-item reset" onClick={() => handleSelect(null)}>
                Reset to my account
              </button>
            )}
            {activeTab === 'db' ? (
              <>
                {filteredDbUsers.slice(0, 50).map(u => (
                  <button
                    key={u.email}
                    className={`branch-picker-item ${u.email.toLowerCase() === overrideEmail?.toLowerCase() ? 'active' : ''}`}
                    onClick={() => handleSelectDbUser(u)}
                  >
                    <span className="branch-code" style={{ fontSize: '0.6rem', minWidth: 'auto' }}>{u.roles.join(', ')}</span>
                    <span className="branch-name">{u.name || u.email}</span>
                  </button>
                ))}
                {filteredDbUsers.length === 0 && <p style={{ textAlign: 'center', color: '#979798', fontSize: '0.75rem', padding: '1rem' }}>No users found</p>}
              </>
            ) : (
              <>
                {loadingContacts && <p style={{ textAlign: 'center', color: '#979798', fontSize: '0.75rem', padding: '1rem' }}>Loading contacts...</p>}
                {filteredContacts.slice(0, 50).map(c => (
                  <button
                    key={`${c.hubspot_id}`}
                    className={`branch-picker-item ${c.email.toLowerCase() === overrideEmail?.toLowerCase() ? 'active' : ''}`}
                    onClick={() => handleSelect(c)}
                  >
                    <span className="branch-code">{c.branchCodes.map(code => `#${code}`).join(', ')}</span>
                    <span className="branch-name">{c.firstname} {c.lastname}</span>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {overrideEmail && isCorporate && (
        <div className="branch-override-badge" onClick={() => setShowPicker(true)}>
          Signed-in as: {currentOverrideName ? `${currentOverrideName.firstname} ${currentOverrideName.lastname}` : overrideEmail}
        </div>
      )}

      <button
        className="auth-status-button"
        onClick={() => setShowMenu(!showMenu)}
      >
        <span className="user-icon"><PersonIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /></span>
        <span className="user-name">{displayName}</span>
      </button>

      {showMenu && (
        <div className="auth-menu">
          <div className="auth-menu-header">
            <strong>{displayName}</strong>
            {user.provider && (
              <small style={{ display: 'block', marginTop: '0.25rem', opacity: 0.7 }}>
                via {user.provider === 'okta' ? 'Okta' : 'Microsoft'}
              </small>
            )}
          </div>
          {isCorporate && (
            <button onClick={() => { setShowMenu(false); setShowPicker(true); }} className="auth-menu-item">
              <SwapHorizIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} /> Switch User
            </button>
          )}
          <button onClick={logout} className="auth-menu-item">
            <LogoutIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} /> Sign Out
          </button>
        </div>
      )}

      {isAdmin && (
        <button
          className="auth-switch-user-mobile"
          onClick={() => setShowPicker(true)}
        >
          <SwapHorizIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} /> Login as another user
        </button>
      )}
    </div>
  );
};

export default AuthStatus;
